import React from 'react'
import { Grid, Theme, Typography } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import useFormikHelper from '../useFormikHelper'
import { ModificationValues } from '../enum'
import {
  biTubeSequencePreviewName,
  useProductFromQueryParam,
  SequenceType,
} from '../ProductsConfiguration'
import { BiTubeProduct, DefaultValue } from '../../BiTube/enum'
import { formatOverhangSequenceRedesign } from '../../../utils/formatOverhangSequence'

const useStyle = makeStyles((theme: Theme) => ({
  textStyle: {
    fontSize: theme.typography.pxToRem(16),
  },
  sequenceLabel: {
    fontWeight: 'bold',
    marginRight: theme.spacing(2),
  },
  sequence: {
    wordBreak: 'break-word',
    display: 'flex',
  },
}))

const BiTubeSequencePreview = () => {
  const classes = useStyle()
  const formik = useFormikHelper()
  const {
    fiveMod,
    sequence,
    threeMod,
    antisenceThreeMod,
    antisenceFiveMod,
    antisenceSequence,
    customerOverhang,
    customerAntisenseOverHang,
  } = formik.selectedOligoValues

  const product = useProductFromQueryParam()
  const previewText = (
    <FormattedMessage
      id="EMPTY_SEQUENCE_PREVIEW_TEXT"
      defaultMessage="Start typing sequence above to see the preview."
    />
  )

  const splitAt = (seq: string, n: number) => {
    const tokens = seq.match(/\[[^\]]+\]|./g) || []
    return {
      antisenseBefore: tokens.slice(0, n).join(''),
      antisenseAfter: tokens.slice(n).join(''),
    }
  }

  const renderSequence = ({
    sequenceData,
    error,
    fiveModValue,
    threeModValue,
    customerOverhangValue,
    sequenceType,
  }: {
    sequenceData?: string
    error?: string
    fiveModValue?: ModificationValues
    threeModValue?: ModificationValues
    customerOverhangValue?: ModificationValues
    sequenceType?: SequenceType
  }) => {
    if (error) return <>{'--'}</>
    if (!sequenceData) return <>{previewText}</>
    const isMirna = product === BiTubeProduct.mirna
    // For MiRna Sense Sequence is Secondary
    const isSenseSequence = sequenceType === SequenceType.Secondary
    let body: React.ReactNode

    const mirnaSliceIndex = 3
    if (isMirna && sequenceData.length > mirnaSliceIndex) {
      if (isSenseSequence) {
        const reversedSequence = sequenceData.split('').reverse().join('')
        const secondPart = reversedSequence.slice(mirnaSliceIndex + 1).split('')
        const bottomString = reversedSequence.slice(
          mirnaSliceIndex,
          mirnaSliceIndex + 1
        )
        const firstPart = reversedSequence.slice(0, mirnaSliceIndex).split('')
        body = (
          <>
            {firstPart}
            <span
              style={{ display: 'inline-block', position: 'relative', top: 11 }}
            >
              {'\\'}
              <span
                style={{
                  display: 'inline-block',
                  position: 'relative',
                  top: 13,
                }}
              >
                {bottomString}
              </span>
              {'/'}
            </span>
            {secondPart}
          </>
        )
      } else {
        // mirna antisense inject a blank space at index 3
        const { antisenseBefore, antisenseAfter } = splitAt(sequenceData, 3)
        let paddinglength = 0
        const threeModset1 = ['[Phos]', '[ThiC6]', '[Btn]', '[ThiC3]']
        const threeModset2 = ['[Cyanine3]', '[Cyanine5]']
        const threeModset3 = ['[AmC7]', '[6FAM]']
        if (threeMod) {
          if (threeModset1.includes(threeMod)) {
            paddinglength = (threeMod.length - 1.5) * 2 * 5
          } else if (threeModset2.includes(threeMod)) {
            paddinglength = (threeMod.length - 2) * 2 * 5
          } else if (threeModset3.includes(threeMod)) {
            paddinglength = (threeMod.length - 0.5) * 2 * 5
          } else {
            paddinglength = threeMod.length * 2 * 4
          }
        }
        if (sequenceData === DefaultValue.sequencePreview) {
          paddinglength = 14
        } else if (sequenceData === '--') {
          paddinglength = 0
        } else {
          paddinglength = 55 + paddinglength
        }
        body = (
          <div style={{ paddingLeft: paddinglength, display: 'inline-block' }}>
            {antisenseBefore}
            <span style={{ display: 'inline-block', width: '3ch' }} />
            {antisenseAfter}
          </div>
        )
      }
    } else {
      body = <>{sequenceData}</>
    }

    const prependModValue =
      isMirna && sequenceType === SequenceType.Secondary
        ? threeModValue
        : fiveModValue

    const appendModValue =
      isMirna && sequenceType === SequenceType.Secondary
        ? fiveModValue
        : threeModValue

    const prependOverhang =
      isMirna && sequenceType === SequenceType.Secondary && customerOverhang
        ? formatOverhangSequenceRedesign(customerOverhang)
        : undefined

    const appendOverhang =
      customerOverhangValue &&
      customerOverhangValue !== ModificationValues.None &&
      !prependOverhang
        ? formatOverhangSequenceRedesign(customerOverhangValue)
        : undefined

    return (
      <>
        {prependModValue !== ModificationValues.None && prependModValue}
        {prependOverhang}
        {body}
        {appendOverhang}
        {appendModValue !== ModificationValues.None && appendModValue}
      </>
    )
  }

  const [primarySequence, secondarySequence] =
    product === BiTubeProduct.mirna
      ? [antisenceSequence, sequence]
      : [sequence, antisenceSequence]

  const [primaryError, secondaryError] =
    product === BiTubeProduct.mirna
      ? [formik.errors?.antisenceSequence, formik.errors?.sequence]
      : [formik.errors?.sequence, formik.errors?.antisenceSequence]

  const [primaryFiveMod, secondaryFiveMod] =
    product === BiTubeProduct.mirna
      ? [antisenceFiveMod, fiveMod]
      : [fiveMod, antisenceFiveMod]

  const [primaryThreeMod, secondaryThreeMod] =
    product === BiTubeProduct.mirna
      ? [antisenceThreeMod, threeMod]
      : [threeMod, antisenceThreeMod]

  const [primaryOverhang, secondaryOverhang] =
    product === BiTubeProduct.mirna
      ? [customerAntisenseOverHang, customerOverhang]
      : [customerOverhang, customerAntisenseOverHang]

  return (
    <Grid container className={classes.textStyle}>
      <Grid item xs={12} className={classes.sequence}>
        <Grid container spacing={0}>
          <Grid item xs={2}>
            <Typography className={classes.sequenceLabel}>
              {biTubeSequencePreviewName(product, SequenceType.Primary)}:
            </Typography>
          </Grid>
          <Grid item xs={10}>
            {renderSequence({
              sequenceData: primarySequence,
              error: primaryError,
              fiveModValue: primaryFiveMod as ModificationValues | undefined,
              threeModValue: primaryThreeMod as ModificationValues | undefined,
              customerOverhangValue: primaryOverhang as
                | ModificationValues
                | undefined,
              sequenceType: SequenceType.Primary,
            })}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} className={classes.sequence}>
        <Grid container spacing={0}>
          <Grid item xs={2}>
            <Typography className={classes.sequenceLabel}>
              {biTubeSequencePreviewName(product, SequenceType.Secondary)}:
            </Typography>
          </Grid>
          <Grid item xs={10}>
            {renderSequence({
              sequenceData: secondarySequence,
              error: secondaryError,
              fiveModValue: secondaryFiveMod as ModificationValues | undefined,
              threeModValue: secondaryThreeMod as
                | ModificationValues
                | undefined,
              customerOverhangValue: secondaryOverhang as
                | ModificationValues
                | undefined,
              sequenceType: SequenceType.Secondary,
            })}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default BiTubeSequencePreview
